import { useEffect, useState } from "react";
import { Eye, ShieldCheck, AlertTriangle, Stethoscope, GraduationCap, Heart } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Parent-facing audit trail for a child's profile.
 *
 * Shows every action by a non-owner actor (helper, therapist, doctor,
 * school_official). Used to answer the question: "Who has touched my
 * child's profile, and when?" — exactly what John asked for.
 *
 * Lives on /privacy/me. Filterable by profile if the parent has more
 * than one child.
 */
function RoleBadge({ role }) {
  const map = {
    therapist: { icon: Stethoscope, label: "Therapist", color: "bg-[color:var(--coral)] text-white" },
    doctor: { icon: Stethoscope, label: "Doctor", color: "bg-[#5BAEDC] text-white" },
    school_official: { icon: GraduationCap, label: "School", color: "bg-[#7CC36B] text-white" },
    school_staff: { icon: GraduationCap, label: "School", color: "bg-[#7CC36B] text-white" },
    caregiver_helper: { icon: Heart, label: "Helper", color: "bg-[color:var(--gold)]" },
  };
  const cfg = map[role] || { icon: Heart, label: role || "Helper", color: "bg-[color:var(--gold)]" };
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border-[2px] border-[color:var(--ink)] text-[10px] font-bold uppercase tracking-[0.15em] ${cfg.color}`}
    >
      <Icon size={10} /> {cfg.label}
    </span>
  );
}

function SeverityRing({ severity }) {
  const colors = {
    ok: "bg-[#D6F5C8]",
    info: "bg-white",
    warn: "bg-[#FFE3E0]",
  };
  return (
    <div
      className={`w-2 h-2 rounded-full border-[2px] border-[color:var(--ink)] shrink-0 ${colors[severity] || "bg-white"}`}
    />
  );
}

export default function ParentActivityPanel() {
  const [items, setItems] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [filter, setFilter] = useState(""); // profile_id filter
  const [loading, setLoading] = useState(true);

  const load = async (profileId) => {
    setLoading(true);
    try {
      const url = profileId
        ? `/privacy/me/activity?profile_id=${encodeURIComponent(profileId)}&limit=200`
        : "/privacy/me/activity?limit=200";
      const { data } = await api.get(url);
      setItems(data?.items || []);
      setProfiles(data?.profiles || []);
    } catch (e) {
      console.error("[ParentActivityPanel] load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filter || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <section className="brutal-card bg-white p-5" data-testid="parent-activity-panel">
      <div className="flex items-center gap-2 mb-2">
        <Eye size={20} className="text-[color:var(--coral-deep)]" />
        <h2 className="display text-xl font-extrabold flex-1">
          Activity by people who help
        </h2>
        <ShieldCheck size={16} className="text-[color:var(--ink-soft)]" />
      </div>
      <p className="text-sm text-[color:var(--ink-soft)] mb-4">
        Every time a therapist, doctor, school staff member, or helper proposes,
        approves, or rejects a change to your child's profile, it shows up here.
        Your own actions are not listed — this view is about who else has touched
        your child's data.
      </p>

      {profiles.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="parent-activity-filters">
          <button
            type="button"
            onClick={() => setFilter("")}
            className={`pill-btn text-xs ${filter === "" ? "primary" : ""}`}
            data-testid="parent-activity-filter-all"
          >
            All children
          </button>
          {profiles.map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() => setFilter(p.id)}
              className={`pill-btn text-xs ${filter === p.id ? "primary" : ""}`}
              data-testid={`parent-activity-filter-${p.id}`}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[color:var(--ink-soft)] italic">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="brutal-card bg-[color:var(--paper-muted)] p-3 text-sm text-[color:var(--ink-soft)] flex items-center gap-2"
          data-testid="parent-activity-empty"
        >
          <AlertTriangle size={14} /> No activity yet. When a helper or clinician
          touches your child's profile, you'll see it here.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="parent-activity-list">
          {items.map((e) => (
            <li
              key={e.id}
              className="brutal-card bg-[color:var(--cream)] p-3 flex items-start gap-3"
              data-testid={`parent-activity-item-${e.id}`}
            >
              <div className="pt-1.5">
                <SeverityRing severity={e.severity} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="font-semibold truncate">{e.actor_name}</span>
                  {e.actor_role && <RoleBadge role={e.actor_role} />}
                  {e.profile_name && profiles.length > 1 && (
                    <span className="text-[11px] text-[color:var(--ink-soft)] uppercase tracking-[0.15em] font-bold">
                      · {e.profile_name}
                    </span>
                  )}
                </div>
                <div className="text-sm">{e.label}</div>
                <div className="text-[11px] text-[color:var(--ink-soft)] mt-0.5">
                  {new Date(e.ts).toLocaleString()}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
